import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { StyledButton } from '../objects/StyledButton';
import { soundManager } from '../systems/SoundManager';
import { transitionManager } from '../systems/TransitionManager';
import { startSession } from '../api/SessionAPI';
import { eventTracker } from '../analytics/EventTracker';
import { setCurrentSessionId } from '../utils/sessionId';
import { detectPlatform } from '../utils/platform';

export class TitleScene extends Phaser.Scene {
  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    soundManager.resume();
    this.createBackground();
    this.createMascot();
    this.createBrand();
    this.createPlayButton();
    this.createFloatingParticles();
    this.createSparkles();

    eventTracker.track('title_enter', { platform: detectPlatform() });
  }

  private createBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }

    const circles = [
      { x: 120, y: 120, r: 80, color: 0xffdad8, alpha: 0.4 },
      { x: 1160, y: 200, r: 120, color: 0x88f2e0, alpha: 0.25 },
      { x: 100, y: 600, r: 140, color: 0xffe173, alpha: 0.2 },
      { x: 1180, y: 650, r: 90, color: 0xff7e7e, alpha: 0.25 },
    ];
    circles.forEach((c) => {
      graphics.fillStyle(c.color, c.alpha);
      graphics.fillCircle(c.x, c.y, c.r);
    });
  }

  private createMascot(): void {
    const mascot = this.add.container(640, 240);

    const faceBg = this.add.circle(0, 0, 80, parseInt(DESIGN.colors.primaryContainer.replace('#', '0x'), 16));
    faceBg.setStrokeStyle(4, parseInt(DESIGN.colors.primary.replace('#', '0x'), 16));
    mascot.add(faceBg);

    mascot.add(this.add.ellipse(-36, 14, 20, 10, 0xffb3b1, 0.6));
    mascot.add(this.add.ellipse(36, 14, 20, 10, 0xffb3b1, 0.6));

    const leftEye = this.add.ellipse(-24, -10, 14, 22, 0x191c20);
    const rightEye = this.add.ellipse(24, -10, 14, 22, 0x191c20);
    mascot.add(leftEye);
    mascot.add(rightEye);

    mascot.add(this.add.circle(-20, -16, 5, 0xffffff));
    mascot.add(this.add.circle(28, -16, 5, 0xffffff));

    const mouth = this.add.graphics();
    mouth.lineStyle(4, 0x191c20, 1);
    mouth.beginPath();
    mouth.arc(0, 10, 18, 0.1 * Math.PI, 0.9 * Math.PI);
    mouth.strokePath();
    mascot.add(mouth);

    this.tweens.add({
      targets: mascot,
      y: 220,
      rotation: 0.08,
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.tweens.add({
      targets: [leftEye, rightEye],
      scaleY: 0.1,
      duration: 120,
      yoyo: true,
      repeat: -1,
      repeatDelay: 2500,
      ease: 'Quad.easeInOut',
    });

    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const orbit = this.add.container(640, 240);
      const particle = this.add.circle(90, 0, 8, [0xff7e7e, 0x88f2e0, 0xffe173][i % 3]);
      orbit.add(particle);
      orbit.setRotation(angle);
      this.tweens.add({
        targets: orbit,
        rotation: angle + Math.PI * 2,
        duration: 4000 + i * 600,
        repeat: -1,
        ease: 'Linear',
      });
      this.tweens.add({
        targets: particle,
        scale: { from: 0.6, to: 1.2 },
        alpha: { from: 0.6, to: 1 },
        duration: 1000 + i * 200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private createBrand(): void {
    this.add.text(640, 420, 'Toybox Arcade', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '96px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 6, 'rgba(132, 34, 40, 0.3)', 0, false, true);

    this.add.text(640, 500, 'Collect 15 cute toys from the claw machine!', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '22px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5);
  }

  private createPlayButton(): void {
    new StyledButton(this, 640, 600, '🎮 PLAY NOW', {
      width: 360,
      height: 80,
      fontSize: '36px',
      onClick: () => this.onPlay(),
    });

    this.add.text(640, 670, 'Tap or click to start', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5);
  }

  private onPlay(): void {
    eventTracker.track('play_now_click');
    const platform = detectPlatform();

    startSession({ platform })
      .then((response) => {
        setCurrentSessionId(response.sessionId);
        eventTracker.init({
          sessionId: response.sessionId,
          anonymousUserId: response.anonymousUserId,
          experimentVersion: response.experimentVersion,
          platform,
        });
        this.registry.set('experimentVersion', response.experimentVersion);
        transitionManager.go(this, 'LoadingScene', { experimentVersion: response.experimentVersion });
      })
      .catch(() => {
        transitionManager.go(this, 'LoadingScene');
      });
  }

  private createFloatingParticles(): void {
    const particleColors = [0xff7e7e, 0x88f2e0, 0xffe173, 0xa43a3d, 0x006b5f];
    for (let i = 0; i < 20; i++) {
      const color = particleColors[Math.floor(Math.random() * particleColors.length)];
      const x = Math.random() * 1280;
      const y = 720 + Math.random() * 200;
      const size = 4 + Math.random() * 8;
      const circle = this.add.circle(x, y, size, color, 0.6);
      this.tweens.add({
        targets: circle,
        y: -50,
        x: x + (Math.random() - 0.5) * 100,
        alpha: 0,
        duration: 3000 + Math.random() * 4000,
        delay: Math.random() * 3000,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private createSparkles(): void {
    for (let i = 0; i < 6; i++) {
      const sparkle = this.add.text(
        640 + (Math.random() - 0.5) * 600,
        360 + (Math.random() - 0.5) * 400,
        '✦',
        {
          fontSize: `${20 + Math.random() * 20}px`,
          color: '#ffe173',
        }
      ).setOrigin(0.5).setAlpha(0.6);

      this.tweens.add({
        targets: sparkle,
        scale: { from: 0.5, to: 1.2 },
        alpha: { from: 0.3, to: 0.9 },
        rotation: Math.PI / 4,
        duration: 1500 + Math.random() * 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Math.random() * 1500,
      });
    }
  }
}
